import type { CSSProperties, ReactNode } from 'react';
import type { Theme } from '../lib/tokens';
import { FONTS } from '../lib/tokens';
import { Icon } from './icons';
import { hapticLight } from '../lib/haptics';

// ─── Logo mark animation keyframes ──────────────────────────────
// Injected once into the document head so the CSS animation is available
// without a stylesheet dependency.
let _logoStyleInjected = false;
function injectLogoStyle() {
  if (_logoStyleInjected || typeof document === 'undefined') return;
  _logoStyleInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    @keyframes dotDrift {
      0%   { transform: translate(0px, 0px); }
      15%  { transform: translate(3px, -5px); }
      30%  { transform: translate(7px, 2px); }
      45%  { transform: translate(2px, 7px); }
      60%  { transform: translate(-4px, 4px); }
      75%  { transform: translate(-6px, -3px); }
      90%  { transform: translate(-2px, -6px); }
      100% { transform: translate(0px, 0px); }
    }
    @keyframes dotPulse {
      0%, 100% { r: 7; opacity: 1; }
      50%       { r: 8.5; opacity: 0.85; }
    }
  `;
  document.head.appendChild(style);
}

// ─── LogoMark — the animated circle + dot ────────────────────────

export function LogoMark({ size = 48, animate = true }: { size?: number; animate?: boolean }) {
  injectLogoStyle();
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Main terracotta circle */}
      <circle cx="50" cy="50" r="46" fill="#C4583C" />
      {/* Floating white dot */}
      <circle
        cx="62"
        cy="40"
        r="7"
        fill="white"
        style={animate ? {
          animation: 'dotDrift 6s ease-in-out infinite, dotPulse 3s ease-in-out infinite',
          transformOrigin: '62px 40px',
        } : undefined}
      />
    </svg>
  );
}

// ─── Logo — mark + wordmark ──────────────────────────────────────

export function Logo({ t, size = 1, animate = true }: { t: Theme; size?: number; animate?: boolean }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 10 * size,
    }}>
      <LogoMark size={32 * size} animate={animate} />
      <span style={{
        fontFamily: FONTS.display, fontSize: 28 * size, fontWeight: 400,
        letterSpacing: -0.5, color: t.ink, fontStyle: 'italic',
        lineHeight: 1,
      }}>
        <span style={{ color: '#C4583C' }}>p</span>reminade
      </span>
    </span>
  );
}

// ─── Chip ────────────────────────────────────────────────────────

export function Chip({ children, t, filled = false, small = false }: {
  children: ReactNode; t: Theme; filled?: boolean; small?: boolean;
}) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: small ? '4px 9px' : '6px 11px',
      borderRadius: 100,
      fontFamily: FONTS.sans, fontSize: small ? 11 : 12.5, fontWeight: 500,
      color: filled ? t.paper : t.inkSoft,
      background: filled ? t.ink : 'transparent',
      border: filled ? 'none' : `1px solid ${t.line}`,
      letterSpacing: 0.1,
      whiteSpace: 'nowrap',
    }}>{children}</span>
  );
}

// ─── TubePill ────────────────────────────────────────────────────

const TUBE_COLORS: Record<string, { bg: string; fg: string }> = {
  'N1':         { bg: '#8A9A7B', fg: '#fff' },
  'Northern':   { bg: '#1F1A17', fg: '#fff' },
  'Overground': { bg: '#E8751A', fg: '#fff' },
  'Central':    { bg: '#C8553D', fg: '#fff' },
  'Victoria':   { bg: '#009FE3', fg: '#fff' },
  'Jubilee':    { bg: '#8491AA', fg: '#fff' },
  'District':   { bg: '#007D32', fg: '#fff' },
  'Circle':     { bg: '#FFD329', fg: '#1F1A17' },
  'Bakerloo':   { bg: '#A0522D', fg: '#fff' },
  'Piccadilly': { bg: '#003EA6', fg: '#fff' },
};

export function TubePill({ line, t }: { line: string; t: Theme }) {
  const c = TUBE_COLORS[line] || { bg: t.ink, fg: t.paper };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '5px 10px', borderRadius: 100,
      background: c.bg, color: c.fg,
      fontFamily: FONTS.sans, fontSize: 11.5, fontWeight: 600, letterSpacing: 0.3,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: 100, background: c.fg, opacity: 0.9 }} />
      {line}
    </span>
  );
}

// ─── SoftButton ──────────────────────────────────────────────────

type ButtonVariant = 'filled' | 'outline' | 'blush' | 'terracotta';

export function SoftButton({ children, onClick, t, variant = 'filled', full, icon, style = {} }: {
  children: ReactNode; onClick?: () => void; t: Theme;
  variant?: ButtonVariant; full?: boolean; icon?: ReactNode; style?: CSSProperties;
}) {
  const variants: Record<ButtonVariant, { bg: string; fg: string; border: string }> = {
    filled:     { bg: t.ink,        fg: t.paper,   border: 'none' },
    outline:    { bg: 'transparent', fg: t.ink,    border: `1px solid ${t.line}` },
    blush:      { bg: t.blush,      fg: '#1F1A17', border: 'none' },
    terracotta: { bg: t.terracotta, fg: '#FBF6EC', border: 'none' },
  };
  const v = variants[variant];
  return (
    <button onClick={onClick} className="pm-press-soft" style={{
      all: 'unset', cursor: 'pointer', textAlign: 'center',
      padding: '15px 22px', borderRadius: 100, boxSizing: 'border-box',
      background: v.bg, color: v.fg, border: v.border,
      fontFamily: FONTS.sans, fontSize: 15, fontWeight: 600, letterSpacing: 0.1,
      width: full ? '100%' : undefined,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
      ...style,
    }}>{icon}{children}</button>
  );
}

// ─── CircleBtn ───────────────────────────────────────────────────

export function CircleBtn({ children, size = 52, t, accent, onClick, style, testId }: {
  children: ReactNode; size?: number; t: Theme; accent?: string; onClick?: () => void; style?: CSSProperties; testId?: string;
}) {
  return (
    <button data-testid={testId} onClick={onClick} className="pm-press" style={{
      all: 'unset', cursor: 'pointer',
      width: size, height: size, borderRadius: 100,
      background: t.card,
      border: `1px solid ${accent ?? t.line}`,
      boxShadow: t.shadow,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
      ...style,
    }}>{children}</button>
  );
}

// ─── IconBtn ─────────────────────────────────────────────────────

export function IconBtn({ children, t, onClick }: { children: ReactNode; t: Theme; onClick?: () => void }) {
  return (
    <button onClick={onClick} className="pm-press" style={{
      all: 'unset', cursor: 'pointer',
      width: 38, height: 38, borderRadius: 100,
      background: t.card, border: `1px solid ${t.line}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>{children}</button>
  );
}

// ─── Label ───────────────────────────────────────────────────────

export function Label({ children, t }: { children: ReactNode; t: Theme }) {
  return (
    <div style={{
      fontFamily: FONTS.sans, fontSize: 10.5, textTransform: 'uppercase',
      letterSpacing: 1.5, color: t.inkMute, fontWeight: 600,
    }}>{children}</div>
  );
}

// ─── TabBar ──────────────────────────────────────────────────────

type TabId = 'discover' | 'matches' | 'me';

export function TabBar({ active, onNav, t, dark, matchBadge }: {
  active: TabId; onNav: (id: TabId) => void; t: Theme; dark: boolean; matchBadge?: boolean;
}) {
  const items: { id: TabId; label: string; icon: (on: boolean) => ReactNode }[] = [
    {
      id: 'discover',
      label: 'Discover',
      icon: (on) => Icon.flame({ s: 24, c: on ? t.terracotta : t.inkMute, w: on ? 2 : 1.6 }),
    },
    {
      id: 'matches',
      label: 'Pairs',
      icon: (on) => Icon.chat({ s: 24, c: on ? t.terracotta : t.inkMute, w: on ? 2 : 1.6, fill: on ? t.blush : 'none' }),
    },
    {
      id: 'me',
      label: 'You',
      icon: (on) => Icon.user({ s: 24, c: on ? t.terracotta : t.inkMute, w: on ? 2 : 1.6, fill: on ? t.blush : 'none' }),
    },
  ];

  return (
    <div style={{
      flexShrink: 0, padding: '12px 20px 6px',
      background: dark ? 'rgba(26,19,17,0.92)' : 'rgba(245,237,225,0.92)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      borderTop: `0.5px solid ${t.line}`,
      display: 'flex', justifyContent: 'space-around', alignItems: 'center',
    }}>
      {items.map(item => {
        const on = active === item.id;
        const showBadge = item.id === 'matches' && matchBadge && !on;
        return (
          <button key={item.id} className="pm-press-soft" onClick={() => { hapticLight(); onNav(item.id); }} style={{
            all: 'unset', cursor: 'pointer', flex: 1,
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
            padding: '6px 0',
          }}>
            <div style={{ position: 'relative', display: 'inline-flex' }}>
              {item.icon(on)}
              {showBadge && (
                <div style={{
                  position: 'absolute', top: -2, right: -4,
                  width: 8, height: 8, borderRadius: '50%',
                  background: t.terracotta,
                  border: `1.5px solid ${dark ? 'rgba(26,19,17,0.92)' : 'rgba(245,237,225,0.92)'}`,
                }} />
              )}
            </div>
            <span style={{
              fontFamily: FONTS.sans, fontSize: 10.5, fontWeight: on ? 600 : 500,
              color: on ? t.ink : t.inkMute, letterSpacing: 0.2,
            }}>{item.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// ─── VitalRow ────────────────────────────────────────────────────

export function VitalRow({ t, label, value, last }: { t: Theme; label: string; value: string; last?: boolean }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '14px 16px', borderBottom: last ? 'none' : `1px solid ${t.line}`,
      fontFamily: FONTS.sans, fontSize: 14,
    }}>
      <span style={{ color: t.inkSoft }}>{label}</span>
      <span style={{ color: t.ink, fontWeight: 500 }}>{value}</span>
    </div>
  );
}

// ─── FakeMap ─────────────────────────────────────────────────────

export function FakeMap({ t, neighborhood }: { t: Theme; neighborhood: string }) {
  // We use CSS filters to make the Google Map match the app's aesthetic
  // t.paper is the background. If it's a dark theme, t.paper is dark.
  const isDark = t.paper === '#1A1311';
  
  const filter = isDark 
    ? 'grayscale(100%) invert(90%) hue-rotate(180deg) contrast(80%)' 
    : 'grayscale(100%) sepia(20%) contrast(90%) opacity(0.8)';

  return (
    <div style={{ position: 'absolute', inset: 0, background: t.paperAlt }}>
      <iframe 
        width="100%" 
        height="100%" 
        frameBorder="0" 
        scrolling="no" 
        marginHeight={0} 
        marginWidth={0} 
        src={`https://maps.google.com/maps?q=${encodeURIComponent(neighborhood)}&t=&z=13&ie=UTF8&iwloc=&output=embed`}
        style={{ filter, pointerEvents: 'none' }}
      />
      <div style={{
        position: 'absolute', bottom: 10, left: 12, right: 12,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        fontFamily: FONTS.mono, fontSize: 11, color: isDark ? '#fff' : t.inkSoft,
        textShadow: isDark ? '0 1px 4px rgba(0,0,0,0.8)' : '0 1px 4px rgba(255,255,255,0.8)',
      }}>
        <span>{neighborhood.toUpperCase()}</span>
      </div>
    </div>
  );
}
